import React, { useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import ReportEngineService from "service/ReportEngineService";
import CustomDropdown, { DropdownOption } from "component/CustomDropdown";
import { PageSizeOption } from "domain/constant/PageSizeOption";

const ReportEngineInformation: React.FC = () => {
  const getStorageKey = (reportId: string) => `reportEngineParams_${reportId}`;
  const { id } = useParams<{ id: string }>();
  const { profile } = useSelector((state: any) => state.auth);
  const token = profile?.data?.token;

  const reportEngineService = ReportEngineService();

  const [detail, setDetail] = useState<any>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [data, setData] = useState<{ columns: string[]; rows: any[][] }>({
    columns: [],
    rows: [],
  });
  const [loading, setLoading] = useState(false);

  const [autoRefresh, setAutoRefresh] = useState<number>(
    Number(localStorage.getItem("reportEngineAutoRefresh") || 0)
  );
  const [lastRefresh, setLastRefresh] = useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingExportCsv, setLoadingExportCsv] = useState(false);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const firstLoad = useRef(true);

  const AUTO_REFRESH_OPTIONS: DropdownOption[] = [
    { id: 0, name: 'Off' },
    { id: 10000, name: '10 seconds' },
    { id: 300000, name: '5 minutes' },
    { id: 600000, name: '10 minutes' },
  ];

  useEffect(() => {
    if (!id) return;
    const savedParams = localStorage.getItem(getStorageKey(id));
    if (savedParams) {
      try {
        const parsed = JSON.parse(savedParams);
        setParams(parsed);
      } catch (err) {
        console.error("Error parsing saved params:", err);
      }
    }
  }, [id]);

  const fetchDetail = async () => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!id) return;

    try {
      const res = await reportEngineService.reportEngineDetail(token, id);
      const detailData = res.data;
      setDetail(detailData);

      const paramObj: Record<string, string> = {};
      if (detailData && Array.isArray(detailData.parameter)) {
        detailData.parameter.forEach((p: string) => (paramObj[p] = ""));
      }
      setParams((prev) => {
        const updatedParams = { ...paramObj, ...prev };
        return updatedParams;
      });
    } catch (err: any) {
      console.error("Error fetching report detail:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail report';
      window.alert(`Error: ${errorMessage}`);
    }
  };

  const fetchReportView = async () => {
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!id) return;

    setLoading(true);
    try {
      const filteredParams: Record<string, any> = {};
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value.trim() !== "") {
          if (!isNaN(Number(value))) filteredParams[key] = Number(value);
          else if (value.toLowerCase() === "true" || value.toLowerCase() === "false")
            filteredParams[key] = value.toLowerCase() === "true";
          else filteredParams[key] = value;
        }
      });

      const res = await reportEngineService.reportEngineView(token, {
        id,
        params: filteredParams,
      });

      const responseData = res.data || { columns: [], rows: [] };

      let filteredRows = responseData.rows;
      if (searchTerm.trim()) {
        filteredRows = filteredRows.filter((row: any[]) =>
          row.some((cell) =>
            String(cell ?? "").toLowerCase().includes(searchTerm.toLowerCase())
          )
        );
      }

      setData({
        columns: responseData.columns || [],
        rows: filteredRows || [],
      });

      const now = new Date();
      const formatted = now.toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      setLastRefresh(`${formatted.replace(",", "")}`);
    } catch (err: any) {
      console.error("Error fetching report view:", err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil data report';
      window.alert(`Error: ${errorMessage}`);
      setData({ columns: [], rows: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id, token]);

  useEffect(() => {
    if (!id) return;
    localStorage.setItem(getStorageKey(id), JSON.stringify(params));
    localStorage.setItem("reportEngineAutoRefresh", String(autoRefresh));
  }, [params, id, autoRefresh]);

  useEffect(() => {
    if (!detail) return;

    const allParamsFilled = !detail.parameter?.length ||
      detail.parameter.every((p: string) => params[p] && params[p].trim() !== "");

    if (firstLoad.current) {
      firstLoad.current = false;
      if (allParamsFilled) {
        fetchReportView();
      }
      return;
    }

    if (!allParamsFilled) return;

    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchReportView();
    }, 1000);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [params, detail]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchReportView, autoRefresh);
    return () => clearInterval(interval);
  }, [autoRefresh, token, id, params]);

  const handleParamChange = (key: string, value: string) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchReportView();
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = data.rows.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(data.rows.length / rowsPerPage);

  const handleExportToCsv = async () => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!detail?.code) {
      window.alert('Kode report tidak ditemukan');
      return;
    }

    setLoadingExportCsv(true);
    try {
      const filteredParams: Record<string, any> = {};
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value.trim() !== "") {
          if (!isNaN(Number(value))) filteredParams[key] = Number(value);
          else if (value.toLowerCase() === "true" || value.toLowerCase() === "false")
            filteredParams[key] = value.toLowerCase() === "true";
          else filteredParams[key] = value;
        }
      });

      const fileType = detail?.type?.toLowerCase() === "csv" ? "csv" : "xlsx";

      const res = await reportEngineService.reportEngineDownload(
        token,
        detail.code,
        filteredParams,
        fileType
      );

      window.alert(res?.message || 'File export sedang diproses dan akan dikirimkan ke email Anda.');
    } catch (error: any) {
      console.error("Error exporting:", error);
      const errorMessage = error.response?.data?.message || error.message || error || "Failed to export";
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setLoadingExportCsv(false);
    }
  };

  const getEmptyParams = () => {
    if (!detail || !detail.parameter) return [];
    return detail.parameter.filter((p: string) => !params[p] || params[p].trim() === "");
  };

  return (
    <div className="page-wrapper">
      <div className="row">
        <div className="col-12">
          <div className="p-4 bg-white rounded shadow-sm">
            <h5 className="mb-3 fw-bold">Report Engine Information</h5>

            {detail && (
              <div className="row mb-4">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="fw-semibold">Code</label>
                    <input type="text" className="form-control" value={detail.code || ""} disabled />
                  </div>
                  {detail.parameter?.map(
                    (p: string, idx: number) =>
                      idx % 2 === 0 && (
                        <div key={idx} className="mb-3">
                          <label className="fw-semibold">{p}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={params[p] || ""}
                            onChange={(e) => handleParamChange(p, e.target.value)}
                          />
                        </div>
                      )
                  )}
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="fw-semibold">Name</label>
                    <input type="text" className="form-control" value={detail.name || ""} disabled />
                  </div>
                  {detail.parameter?.map(
                    (p: string, idx: number) =>
                      idx % 2 === 1 && (
                        <div key={idx} className="mb-3">
                          <label className="fw-semibold">{p}</label>
                          <input
                            type="text"
                            className="form-control"
                            value={params[p] || ""}
                            onChange={(e) => handleParamChange(p, e.target.value)}
                          />
                        </div>
                      )
                  )}
                </div>
              </div>
            )}

            <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2 overflow-visible">
              <form className="d-flex align-items-center" onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  className="form-control me-2"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    minWidth: "120px",
                    height: "50px"
                  }}
                />
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ height: "50px" }}
                >
                  <i className="fa-solid fa-search"></i>
                </button>
              </form>

              <div className="ms-auto d-flex align-items-center flex-wrap gap-2">
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-primary d-flex justify-content-center align-items-center"
                    style={{ height: "50px" }}
                    onClick={handleExportToCsv}
                    disabled={loadingExportCsv}
                  >
                    {loadingExportCsv && (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </>
                    )}
                    Export To {detail?.type?.toUpperCase() || "FILE"}
                  </button>
                </div>
                <label className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>Auto Refresh</label>
                <CustomDropdown
                  value={autoRefresh}
                  options={AUTO_REFRESH_OPTIONS}
                  onChange={(opt) => setAutoRefresh(Number(opt.id))}
                  containerClassName="me-1"
                  align="right"
                  buttonStyle={{
                    width: "130px",
                    height: "50px",
                    padding: "6px 12px"
                  }}
                />
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={fetchReportView}
                  style={{ height: "50px" }}
                >
                  <i className="fa fa-rotate"></i>
                </button>
              </div>
            </div>

            <p className="text-end text-muted mb-3" style={{ fontSize: "0.85rem" }}>
              Last refresh at {lastRefresh || "-"}
            </p>

            <div style={{ width: "100%", overflowX: "auto" }}>
              <table className="table table-condensed table-hover">
                <thead className="table-primary">
                  <tr>
                    <th className="text-start">No</th>
                    {data.columns.map((col, idx) => (
                      <th key={idx} className="text-center">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {!loading ? (
                    (() => {
                      const emptyParams = getEmptyParams();
                      if (emptyParams.length > 0) {
                        return (
                          <tr>
                            <td colSpan={data.columns.length + 1 || 1} className="text-center text-muted py-4">
                              Please fill this parameter: <strong>{emptyParams.join(", ")}</strong>
                            </td>
                          </tr>
                        );
                      }
                      return currentRows.length > 0 ? (
                        currentRows.map((row, rIdx) => (
                          <tr key={rIdx}>
                            <td>{(currentPage - 1) * rowsPerPage + rIdx + 1}</td>
                            {row.map((cell, cIdx) => (
                              <td key={cIdx} className="text-center">{String(cell ?? "-")}</td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={data.columns.length + 1 || 1} className="text-center text-muted py-4">
                            No data available
                          </td>
                        </tr>
                      );
                    })()
                  ) : (
                    <tr>
                      <td colSpan={data.columns.length + 1 || 1} className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-end align-items-center mt-3">
              <div className="d-flex align-items-center">
                <div className="me-4 d-flex align-items-center">
                  <label className="me-2">Rows per page:</label>
                  <CustomDropdown
                    value={rowsPerPage}
                    options={PageSizeOption.options}
                    onChange={(opt) => {
                      setRowsPerPage(Number(opt.id));
                      setCurrentPage(1);
                    }}
                    align="top"
                  />
                </div>
                <span className="me-3">
                  {`${(currentPage - 1) * rowsPerPage + 1}–${Math.min(
                    currentPage * rowsPerPage,
                    data.rows.length
                  )} of ${data.rows.length}`}
                </span>

                <nav>
                  <ul className="pagination mb-0">
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-left"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-left"></i>
                      </button>
                    </li>
                    <li className="page-item disabled">
                      <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)} style={{ border: 0 }}>
                        <i className="fa fa-angle-right"></i>
                      </button>
                    </li>
                    <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={{ border: 0 }}>
                        <i className="fa fa-angle-double-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportEngineInformation;
